import React from 'react';
import { Box, Typography } from '@mui/material';

import {
    COLOR_BLUE,
    COLOR_GREY_DARK,
    COLOR_TEAL,
    COLOR_WHITEISH
} from '../../../theme';

type PropsType = {
    title: string,
    dimmed?: boolean,
    tags?: React.ReactNode,
    secondary?: string,
    action?: React.ReactNode,
    onClick?: () => void
};

/** Row styled like the grouped rows in Notifications: title, tags + muted line, action to the right. */
export default function ListRow({
    title,
    dimmed = false,
    tags,
    secondary,
    action,
    onClick
}: PropsType) {
    return (
        <Box
            role={onClick ? 'link' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onClick={onClick}
            onKeyDown={onClick ? (e) => {
                if (e.key === 'Enter' && e.target === e.currentTarget) {
                    onClick();
                }
            } : undefined}
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 1.5, sm: 2 },
                px: { xs: 1.5, sm: 2 },
                py: 2,
                cursor: onClick ? 'pointer' : 'default',
                borderBottom: `1px solid ${COLOR_WHITEISH}`,
                '&:last-child': { borderBottom: 'none' },
                ...(onClick ? {
                    '&:hover': { bgcolor: COLOR_WHITEISH },
                    '&:focus-visible': { outline: `2px solid ${COLOR_TEAL}`, outlineOffset: -2 }
                } : {})
            }}
        >
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    sx={{
                        fontWeight: 700,
                        fontSize: '1rem',
                        color: COLOR_BLUE,
                        opacity: dimmed ? 0.7 : 1,
                        overflowWrap: 'anywhere'
                    }}
                >
                    {title}
                </Typography>
                {(tags || secondary) && (
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5
                    }}
                    >
                        {tags}
                        {secondary && (
                            <Typography variant="body2" sx={{ color: COLOR_GREY_DARK }}>
                                {secondary}
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
            {action}
        </Box>
    );
}
